import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import WabaListView from "@/pages/WhatsApp/WabaListView";
import WabaWizard from "@/pages/WhatsApp/WabaWizard";
import WabaDetailView from "@/pages/WhatsApp/WabaDetailView";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { NumerosWabaTab } from "@/components/whatsapp/NumerosWabaTab";
import { PruebasTab } from "@/components/whatsapp/PruebasTab";
import { NotificacionesTab } from "@/components/whatsapp/NotificacionesTab";
import { ConexionEstableTab } from "@/components/whatsapp/ConexionEstableTab";
import EmbeddedSignupCard from "@/components/whatsapp/EmbeddedSignupCard";
import { FlowsTab } from "@/components/whatsapp/FlowsTab";
import HelpTip from "@/components/HelpTip";
import { Plus, MessageCircle, Phone, FlaskConical, BellRing, Building2, Shield, Workflow } from "lucide-react";

const SECTIONS_ALL = [
  { id: "cuentas", label: "Cuentas WABA", icon: Building2 },
  { id: "numeros", label: "Números y credenciales", icon: Phone },
  { id: "flows", label: "Flows", icon: Workflow },
  { id: "pruebas", label: "Pruebas", icon: FlaskConical },
  { id: "notificaciones", label: "Notificaciones", icon: BellRing },
  { id: "conexion", label: "Conexión estable", icon: Shield },
];

export default function WhatsAppPage() {
  const [params, setParams] = useSearchParams();
  // "Conexion estable" (proxies) solo aplica a Evolution. Se muestra solo si el
  // tenant tiene alguna cuenta Evolution; para Gupshup/Twilio estorba y confunde.
  const [usesEvolution, setUsesEvolution] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/waba-accounts")
      .then((r) => {
        const accs = Array.isArray(r.data) ? r.data : [];
        if (alive) setUsesEvolution(accs.some((a) => String(a?.provider || "").toLowerCase() === "evolution"));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const SECTIONS = SECTIONS_ALL.filter((s) => s.id !== "conexion" || usesEvolution);
  const tab = SECTIONS.some((s) => s.id === params.get("seccion")) ? params.get("seccion") : "cuentas";
  const [wizardOpen, setWizardOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedWaba, setSelectedWaba] = useState(null);

  const setTab = (id) => {
    const next = new URLSearchParams(params);
    if (id === "cuentas") next.delete("seccion");
    else next.set("seccion", id);
    setParams(next, { replace: true });
  };

  if (selectedWaba) {
    return (
      <div className="space-y-6" data-testid="whatsapp-page">
        <WabaDetailView waba={selectedWaba} onBack={() => setSelectedWaba(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2" data-testid="whatsapp-page-title">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
            WhatsApp WABA
            <HelpTip
              title="Un solo lugar"
              content="Aquí configuras todo tu WhatsApp: cuentas, proveedor, números, pruebas y avisos. Es la pestaña WhatsApp de tu Configuración."
            />
          </h1>
          <p className="text-xs text-muted-foreground mt-1">Cuentas, números, pruebas y avisos al equipo. Todo el WhatsApp del tenant.</p>
        </div>
        {tab === "cuentas" && (
          <Button onClick={() => setWizardOpen(true)} className="gap-2" data-testid="waba-new-account-btn">
            <Plus className="h-4 w-4" />
            Nueva cuenta
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="whatsapp-subtabs-nav">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const on = tab === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setTab(s.id)}
              className={`px-3 py-2 rounded-xl text-xs border flex items-center gap-1.5 transition ${on ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-black/20 text-zinc-300 hover:border-white/20"}`}
              data-testid={`whatsapp-subtab-button-${s.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div data-testid={`whatsapp-subtab-content-${tab}`}>
        {tab === "cuentas" && (
          <div className="space-y-6">
            <EmbeddedSignupCard onConnected={() => setRefreshKey((k) => k + 1)} />
            <WabaListView refreshKey={refreshKey} onSelectWaba={setSelectedWaba} />
          </div>
        )}
        {tab === "numeros" && <NumerosWabaTab />}
        {tab === "flows" && <FlowsTab />}
        {tab === "pruebas" && <PruebasTab />}
        {tab === "notificaciones" && <NotificacionesTab />}
        {tab === "conexion" && usesEvolution && <ConexionEstableTab />}
      </div>

      <WabaWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
